"use client";

import { CHROME_EXTENSION_ID } from "@/constants/chromeExtensionId";
import { sendToExtension, type ExtensionRuntimeLike } from "@/lib/utils/sendToExtension";

export type SessionLifecycleHintEvent =
  | "started"
  | "ended"
  | "paused"
  | "resumed";

interface ChromeLike {
  runtime?: ExtensionRuntimeLike;
}

interface SessionLifecycleHintMessage {
  action: "sessionLifecycleHint";
  event: SessionLifecycleHintEvent;
  source: "web-app";
  sentAt: number;
}

function createLifecycleHintMessage(
  event: SessionLifecycleHintEvent
): SessionLifecycleHintMessage {
  return {
    action: "sessionLifecycleHint",
    event,
    source: "web-app",
    sentAt: Date.now(),
  };
}

const LIFECYCLE_HINT_RETRY_DELAYS_MS = [0, 120, 350, 800] as const;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Sends a best-effort lifecycle hint to the Chrome extension.
 * Older extension builds may not handle this message, so failures are silent.
 * Retries a few times to tolerate a cold service worker right after browser launch.
 *
 * @param event - The confirmed session lifecycle event to forward
 * @returns Whether the message was accepted by the extension runtime
 */
export async function sendSessionLifecycleHintToExtension(
  event: SessionLifecycleHintEvent
): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  const chromeLike = (window as Window & { chrome?: ChromeLike }).chrome;
  const runtime = chromeLike?.runtime;
  if (!runtime?.sendMessage) {
    return false;
  }

  const payload = createLifecycleHintMessage(event);

  for (let i = 0; i < LIFECYCLE_HINT_RETRY_DELAYS_MS.length; i++) {
    if (i > 0) {
      await delay(LIFECYCLE_HINT_RETRY_DELAYS_MS[i]!);
    }

    // Through sendToExtension (TX-5557): a WebKit shim's rejected Promise is
    // a failed try, not an unhandled rejection or a hint waiting forever.
    const reply = await sendToExtension(runtime, CHROME_EXTENSION_ID, payload);

    if (reply.ok) {
      return true;
    }
  }

  return false;
}
